import { createContext, type ReactNode, useMemo, useState } from "react"

export type FileExplorerSelectedFile = {
  path: string[]
  contents: ReactNode | { __html: string }
  language?: string
}

type FileExplorerContextContent = {
  selected: FileExplorerSelectedFile | null
  setSelected: (selected: FileExplorerSelectedFile | null) => void
}

export const FileExplorerContext = createContext<FileExplorerContextContent>({
  selected: null,
  setSelected: () => {},
})

type FileExplorerProviderProps = {
  children: ReactNode
  onSelectedChange?: (selected: FileExplorerSelectedFile | null) => void
} & (
  | { selected?: FileExplorerSelectedFile | null; defaultSelected?: never }
  | { defaultSelected?: FileExplorerSelectedFile | null; selected?: never }
)

export function FileExplorerProvider(props: FileExplorerProviderProps) {
  const {
    selected: externalSelected,
    defaultSelected = null,
    children,
    onSelectedChange,
  } = props

  const { selected, setSelected } = useFileExplorer(defaultSelected)

  const value = useMemo(() => {
    if (externalSelected !== undefined)
      return {
        selected: externalSelected,
        setSelected: (selected: FileExplorerSelectedFile | null) =>
          onSelectedChange?.(selected),
      }

    return {
      selected,
      setSelected: (selected: FileExplorerSelectedFile | null) => {
        setSelected(selected)
        onSelectedChange?.(selected)
      },
    }
  }, [externalSelected, selected, setSelected, onSelectedChange])

  return (
    <FileExplorerContext.Provider value={value}>
      {children}
    </FileExplorerContext.Provider>
  )
}

export function useFileExplorer(
  defaultSelected: FileExplorerSelectedFile | null = null,
) {
  const [selected, setSelected] = useState<FileExplorerSelectedFile | null>(
    defaultSelected,
  )

  return useMemo(() => ({ selected, setSelected }), [selected])
}
